import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { IMAGES, DIAGNOSTIC_TOOLS } from "@/data/content";
import { Reveal } from "@/components/Reveal";

const CountUp = ({ to, suffix = "", decimals = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf; const start = performance.now(); const dur = 1400;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(to * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);
  return <span ref={ref}>{val.toFixed(decimals)}{suffix}</span>;
};

const STATS = [
  { label: "Data points per diagnosis", to: 40, suffix: "+" },
  { label: "Service radius (mi)", to: 20, suffix: "" },
  { label: "Ton systems serviced", to: 25, suffix: "" },
  { label: "Labor warranty (yr)*", to: 1, suffix: "" },
];

export const TechSection = () => (
  <section data-testid="tech-section" className="relative overflow-hidden border-t border-white/10 bg-panel/40 py-24 md:py-32">
    <div className="dot-grid absolute inset-0 opacity-40" />
    <div className="relative mx-auto grid max-w-[1440px] items-center gap-14 px-6 md:px-12 lg:grid-cols-2">
      <Reveal>
        <div className="relative border border-white/10">
          <img src={IMAGES.diagnostics} alt="Digital manometer diagnostic reading" className="aspect-[4/3] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent" />
          {/* synthetic telemetry overlay */}
          <div className="absolute left-4 top-4 border border-brand-cyan/40 bg-ink/70 px-3 py-2 backdrop-blur-sm">
            <div className="font-mono text-[10px] uppercase tracking-widest text-brand-cyan">LIVE READ</div>
            <div className="font-mono text-lg text-white">248 <span className="text-xs text-slatex-300">ppm CO</span></div>
          </div>
          <div className="absolute bottom-4 right-4 flex gap-2">
            {[0, 1, 2].map((i) => <span key={i} className="h-1 w-6 bg-brand-cyan/60" style={{ opacity: 1 - i * 0.3 }} />)}
          </div>
        </div>
      </Reveal>

      <div>
        <Reveal>
          <div className="mb-4 flex items-center gap-3">
            <span className="h-px w-10 bg-brand-cyan" />
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-brand-cyan">Technology & Diagnostics</span>
          </div>
          <h2 className="font-display text-4xl font-extrabold leading-[0.98] tracking-tight md:text-5xl">
            We diagnose systems.<br /><span className="text-slatex-300">We don't guess at them.</span>
          </h2>
          <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-slatex-300">
            Genuine Forge uses measurable data — pressure, temperature, current, voltage, airflow, system sequence, and fault codes — to find the real fault the first time.
          </p>
        </Reveal>

        <div className="mt-10 grid grid-cols-2 gap-px border border-white/10 bg-white/10">
          {DIAGNOSTIC_TOOLS.map((t, i) => (
            <motion.div key={t.label} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
              transition={{ delay: i * 0.06 }} className="bg-ink p-5">
              <div className="font-mono text-[10px] uppercase tracking-widest text-brand-cyan">{t.metric}</div>
              <div className="mt-1 font-display text-sm font-semibold text-white">{t.label}</div>
              <div className="mt-1 font-mono text-[11px] text-slatex-500">{t.unit}</div>
            </motion.div>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-4 gap-4">
          {STATS.map((s) => (
            <div key={s.label}>
              <div className="font-display text-3xl font-extrabold text-white md:text-4xl"><CountUp to={s.to} suffix={s.suffix} /></div>
              <div className="mt-1 font-mono text-[10px] uppercase leading-tight tracking-wide text-slatex-500">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default TechSection;
